import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Trophy, Star, Zap, Target, Award, Crown,
  Flame, Shield, Rocket, Brain, Lock, CheckCircle,
  Sparkles, Medal, Gift, Package, Loader2
} from 'lucide-react';
import useUserStore from '../store/userStore';

// Icon mapping from backend icon names
const iconMap = {
  target: Target,
  star: Star,
  crown: Crown,
  rocket: Rocket,
  package: Package,
  flame: Flame,
  zap: Zap,
  shield: Shield,
  brain: Brain,
  award: Award,
  medal: Medal,
  trophy: Trophy,
};

const tierColors = {
  bronze: 'from-amber-600 to-amber-800',
  silver: 'from-gray-400 to-gray-600',
  gold: 'from-yellow-400 to-yellow-600',
  legendary: 'from-purple-500 to-pink-500',
};

const tierBgColors = {
  bronze: 'bg-amber-100',
  silver: 'bg-gray-100',
  gold: 'bg-yellow-100',
  legendary: 'bg-purple-100',
};

const tierBorderColors = {
  bronze: 'border-amber-300',
  silver: 'border-gray-300',
  gold: 'border-yellow-300',
  legendary: 'border-purple-300',
};

const AchievementCard = ({ achievement, onClaim, isClaiming }) => {
  const Icon = iconMap[achievement.icon] || Trophy;
  const progressPercent = Math.min(achievement.progress * 100, 100);
  const unlocked = achievement.is_unlocked;
  const claimed = achievement.is_claimed;

  return (
    <div
      className={`relative rounded-xl border-2 p-4 transition-all duration-300 ${
        unlocked
          ? `${tierBgColors[achievement.tier]} ${tierBorderColors[achievement.tier]} shadow-lg`
          : 'bg-slate-800/50 border-slate-700 opacity-75'
      }`}
    >
      {/* Tier badge */}
      <div className={`absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-xs font-bold text-white bg-gradient-to-r ${tierColors[achievement.tier]}`}>
        {achievement.tier.toUpperCase()}
      </div>

      <div className="flex items-start gap-3">
        <div className={`p-3 rounded-lg ${unlocked ? `bg-gradient-to-br ${tierColors[achievement.tier]}` : 'bg-gray-200'}`}>
          {unlocked ? (
            <Icon className="w-6 h-6 text-white" />
          ) : (
            <Lock className="w-6 h-6 text-gray-400" />
          )}
        </div>

        <div className="flex-1">
          <h3 className={`font-bold ${unlocked ? 'text-gray-900' : 'text-slate-400'}`}>
            {achievement.name}
          </h3>
          <p className={`text-sm mb-2 ${unlocked ? 'text-gray-600' : 'text-slate-500'}`}>{achievement.description}</p>

          {/* Progress bar */}
          <div className={`h-2 rounded-full overflow-hidden mb-2 ${unlocked ? 'bg-gray-200' : 'bg-slate-700'}`}>
            <div
              className={`h-full transition-all duration-500 bg-gradient-to-r ${tierColors[achievement.tier]}`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div className="flex items-center justify-between">
            <span className={`text-xs ${unlocked ? 'text-gray-500' : 'text-slate-500'}`}>
              {achievement.current_value} / {achievement.requirement_value}
            </span>

            {unlocked && !claimed && (
              <button
                onClick={() => onClaim(achievement.id)}
                disabled={isClaiming}
                className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-full hover:from-green-600 hover:to-emerald-700 transition-all animate-pulse disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isClaiming ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <Gift className="w-3 h-3" />
                    Claim +{achievement.reward_dp} DP
                  </>
                )}
              </button>
            )}

            {claimed && (
              <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                <CheckCircle className="w-4 h-4" />
                Claimed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Sparkle effect for unlocked */}
      {unlocked && !claimed && (
        <Sparkles className="absolute top-2 left-2 w-4 h-4 text-yellow-400 animate-ping" />
      )}
    </div>
  );
};

const Achievements = () => {
  const { achievements, achievementsLoaded, fetchAchievements, claimAchievement, user } = useUserStore();
  const [isLoading, setIsLoading] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [filterTier, setFilterTier] = useState('all');

  // Fetch achievements on mount
  useEffect(() => {
    const loadAchievements = async () => {
      // Skip for demo mode
      const token = localStorage.getItem('token');
      if (token === 'demo_token') return;

      if (!achievementsLoaded) {
        setIsLoading(true);
        try {
          await fetchAchievements();
        } catch (error) {
          console.error('Failed to load achievements:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadAchievements();
  }, [achievementsLoaded, fetchAchievements]);

  const handleClaim = async (achievementId) => {
    setClaimingId(achievementId);
    try {
      const result = await claimAchievement(achievementId);
      toast.success(`Achievement claimed! +${result.reward_dp} DP`);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    } catch (error) {
      toast.error('Failed to claim achievement');
    } finally {
      setClaimingId(null);
    }
  };

  // Filter achievements by tier
  const filteredAchievements = filterTier === 'all'
    ? achievements
    : achievements.filter(a => a.tier === filterTier);

  const unlockedCount = achievements.filter(a => a.is_unlocked).length;
  const totalCount = achievements.length;

  // Demo mode fallback
  const isDemoMode = localStorage.getItem('token') === 'demo_token';
  if (isDemoMode) {
    return (
      <div className="text-center py-12">
        <Trophy className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-600 mb-2">Achievements</h2>
        <p className="text-gray-500">
          Sign up for a real account to track achievements and earn rewards!
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Confetti effect */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10px',
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              <div
                className="w-3 h-3 rounded-sm"
                style={{
                  backgroundColor: ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444'][Math.floor(Math.random() * 5)],
                  transform: `rotate(${Math.random() * 360}deg)`,
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 rounded-xl p-6 mb-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Trophy className="w-8 h-8" />
              Achievements
            </h2>
            <p className="text-purple-100 mt-1">
              Complete challenges to unlock achievements and earn bonus DP!
            </p>
          </div>

          <div className="text-right">
            <div className="text-4xl font-bold">{unlockedCount}/{totalCount}</div>
            <div className="text-sm text-purple-100">Unlocked</div>
          </div>
        </div>

        {/* Overall progress */}
        <div className="mt-4">
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-1000"
              style={{ width: `${totalCount > 0 ? (unlockedCount / totalCount) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {['all', 'bronze', 'silver', 'gold', 'legendary'].map(tier => (
          <button
            key={tier}
            onClick={() => setFilterTier(tier)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filterTier === tier
                ? tier === 'all'
                  ? 'bg-slate-700 text-white'
                  : `bg-gradient-to-r ${tierColors[tier]} text-white`
                : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {tier.charAt(0).toUpperCase() + tier.slice(1)}
            {tier !== 'all' && (
              <span className="ml-1 opacity-75">
                ({achievements.filter(a => a.tier === tier).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Achievements Grid */}
      {filteredAchievements.length === 0 ? (
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400">No achievements found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAchievements.map(achievement => (
            <AchievementCard
              key={achievement.id}
              achievement={achievement}
              onClaim={handleClaim}
              isClaiming={claimingId === achievement.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Achievements;
